import type { AgentRunModel } from "./messages"
import type { ChatSessionModel } from "../models"

import { getManagedCredential } from "../auth-refresh-coordinator"
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/**
 * Client for the end-user hosted-agents API (HERMES, Fas 4).
 *
 * The backend filters both endpoints on the caller's AccessPolicy and returns
 * a uniform 404 for agents the user may not see — so a missing agent and a
 * denied agent are indistinguishable by design.
 *
 * Every call carries the managed bearer on its FIRST attempt
 * (`idToken ?? getManagedCredential()`, as the artifacts client does). The
 * roster/thread hooks call these without a token; sending none made every
 * poll and thread switch a 401 -> refresh -> replay round trip (#3207). The
 * 401 recovery in `fetchWithAuthRetry` stays as the fallback only.
 */

/** B6: per-agent memory model. Session `visibility` is derived from this SERVER-SIDE at creation. */
export type HostedAgentMemoryMode = "personal" | "open"

/** B6: stamped on the session when it is created; never changed retroactively. */
export type HostedAgentSessionVisibility = "personal" | "open"

export type HostedAgentAvatar = {
  /** Hex color, e.g. "#6366f1". */
  color: string
  /** Up to 3 characters; may be empty — derive from the name then. */
  initials: string
  /** Explicit approved character; omitted definitions keep their legacy stable-ID portrait. */
  character?: "nova" | "atlas" | "milo" | "alma" | "cleo" | "vega" | "otis" | "iris" | null
}

export type HostedAgentProfile = {
  name: string
  title: string
  description: string
  /** Brief user-facing promise, separate from routing/mission description. */
  introduction?: string
  /** Administrator-written suggestions. Clicking inserts a draft, never runs it. */
  starter_prompts?: string[]
  avatar: HostedAgentAvatar
}

/** End-user-safe projection of a `HostedAgentDefinition` (no instructions/secrets/budget). */
export type HostedAgentSummary = {
  /** Canonical id, `agent:<uuid>`. Sent as `hosted_agent_id` to `/create-session`. */
  id: string
  /** URL-safe reference (`[a-z0-9-]`), unique per tenant. Used in `/agents/:agentSlug`. */
  slug: string
  /** Always `true` here — the roster endpoint filters disabled agents out server-side. */
  enabled: boolean
  profile: HostedAgentProfile
  memory_mode: HostedAgentMemoryMode
}

type HostedAgentListResponse = {
  agents?: HostedAgentSummary[]
  count?: number
}

/**
 * A chat session bound to a hosted agent. Regular `ChatSessionModel` plus the
 * HERMES stamps from `create-session` (see `chatsessionstorage.py`). Every
 * row carries `currentUserRole`: `"owner"` on the caller's own threads,
 * `"reader"` on another user's open-stamped threads (B6: readable by the
 * agent's audience, writable by the owner only).
 */
export type HostedAgentSession = ChatSessionModel & {
  hostedAgentId?: string
  visibility?: HostedAgentSessionVisibility
  /** Public run fields on the caller's OWN threads; never on foreign open threads. */
  agent_run?: Partial<AgentRunModel> | null
}

/**
 * QUERY: Lists the hosted agents the current user may see (enabled + AccessPolicy-filtered server-side).
 * @param idToken - Optional authentication token
 * @returns End-user-safe agent summaries
 * @category Query
 */
export async function listHostedAgentsApi(idToken?: string): Promise<HostedAgentSummary[]> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/agents`,
    {
      method: "GET",
      headers: getHeaders(idToken ?? getManagedCredential()),
    },
    idToken
  )

  const payload = (await response.json()) as HostedAgentListResponse
  return payload.agents ?? []
}

/**
 * QUERY: Lists the current user's visible threads for one hosted agent.
 *
 * The backend scopes the list per B6: the caller's own sessions plus, for
 * `memory_mode: "open"` agents, sessions stamped `visibility: "open"`. The
 * thread list UI additionally groups/filters on each session's stamped
 * `visibility` (see `features/agents/utils/agents-utils.ts`).
 *
 * @param slug - Agent slug (or canonical id — the backend resolves both)
 * @param idToken - Optional authentication token
 * @returns Sessions bound to the agent, newest first
 * @category Query
 */
export async function listHostedAgentSessionsApi(
  slug: string,
  idToken?: string
): Promise<HostedAgentSession[]> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/agents/${encodeURIComponent(slug)}/sessions`,
    {
      method: "GET",
      headers: getHeaders(idToken ?? getManagedCredential()),
    },
    idToken
  )

  // The backend returns a bare array (same contract as /api/list-sessions).
  const payload = (await response.json()) as HostedAgentSession[]
  return Array.isArray(payload) ? payload : []
}

// -----------------------------------------------------------------------------
// Working state (#3023) — structured facts the agent has established.
// -----------------------------------------------------------------------------

export type AgentWorkingStateValueType = "number" | "string" | "boolean" | "json"

/** Who established the value: an agent run, or a human correction. */
export type AgentWorkingStateProvenance = {
  actor: "agent" | "user"
  user_id?: string | null
  run_id?: string | null
  session_id?: string | null
  initiator?: string | null
  recorded_at: string
}

/** The confirmed (or human-set) value of one fact, with source and validity. */
export type AgentWorkingStateValue = {
  value: unknown
  value_type: AgentWorkingStateValueType
  unit?: string | null
  source: string
  valid_until?: string | null
  note?: string | null
  provenance: AgentWorkingStateProvenance
}

/**
 * One working-state fact in the END-USER shape: the active value only —
 * pending proposals are unconfirmed and never leave the admin surface, so the
 * user view carries only their count.
 */
export type AgentWorkingStateFact = {
  /** `fact:<agent-uuid>:<key>` */
  id: string
  agent_id: string
  key: string
  label?: string | null
  active: AgentWorkingStateValue | null
  /** True after a human correction: agent proposals can never replace the value. */
  pinned: boolean
  pending_count: number
  created_at: string
  updated_at: string
}

type AgentWorkingStateListResponse = {
  facts?: AgentWorkingStateFact[]
  count?: number
}

/**
 * QUERY: The agent's active working-state facts (B6: agent-scoped — everyone
 * in the agent's audience sees the same established numbers, independent of
 * thread visibility).
 * @category Query
 */
export async function fetchAgentWorkingState(
  slug: string,
  idToken?: string
): Promise<AgentWorkingStateFact[]> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/agents/${encodeURIComponent(slug)}/working-state`,
    {
      method: "GET",
      headers: getHeaders(idToken ?? getManagedCredential()),
    },
    idToken
  )
  const payload = (await response.json()) as AgentWorkingStateListResponse
  return payload.facts ?? []
}
